import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, User, Mail, Phone, MessageSquare, Check, ChevronDown } from 'lucide-react';
import { useBooking } from '../../contexts/BookingContext';
import { treatments, treatmentCategories, getAllCategories } from '../../data/treatmentsPageData';
import { useTreatmentTranslations } from '../../hooks/useTreatmentTranslations';
import { colors, spacing, typography, shadows, transitions, easings } from '../../styles/designSystem';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[4]};
  
  @media (max-width: 768px) {
    top: 72px;
    align-items: flex-start;
    padding: ${spacing[4]} ${spacing[3]} ${spacing[6]};
    overflow-y: auto;
  }
  
  @media (max-width: 480px) {
    padding: ${spacing[3]} ${spacing[2]} ${spacing[4]};
  }
`;

const ModalContainer = styled(motion.div)`
  background: white;
  border-radius: ${spacing[6]};
  box-shadow: ${shadows.magical};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    max-height: calc(100vh - 120px);
    border-radius: ${spacing[4]};
    margin-top: ${spacing[4]};
  }
  
  @media (max-width: 480px) {
    max-height: calc(100vh - 100px);
    border-radius: ${spacing[3]};
    margin-top: ${spacing[2]};
  }
`;

const ModalHeader = styled.div`
  padding: ${spacing[6]} ${spacing[6]} ${spacing[4]};
  border-bottom: 1px solid ${colors.sacred.mist};
  position: relative;
  
  @media (max-width: 768px) {
    padding: ${spacing[4]} ${spacing[4]} ${spacing[3]};
  }
  
  @media (max-width: 480px) {
    padding: ${spacing[3]} ${spacing[3]} ${spacing[2]};
  }
`;

const ModalTitle = styled.h2`
  font-family: ${typography.fonts.display};
  font-size: ${typography.sizes.xl};
  font-weight: ${typography.weights.bold};
  color: ${colors.sacred.shadow};
  margin: 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: ${typography.sizes.lg};
  }
  
  @media (max-width: 480px) {
    font-size: ${typography.sizes.md};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${spacing[4]};
  right: ${spacing[4]};
  background: none;
  border: none;
  padding: ${spacing[2]};
  cursor: pointer;
  border-radius: 50%;
  color: ${colors.sacred.shadow};
  transition: ${transitions.gentle};
  
  @media (max-width: 768px) {
    top: ${spacing[3]};
    right: ${spacing[3]};
    padding: ${spacing[1]};
  }
  
  &:hover {
    background: ${colors.sacred.whisper};
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: ${spacing[6]};
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  
  @media (max-width: 768px) {
    padding: ${spacing[4]};
  }
  
  @media (max-width: 480px) {
    padding: ${spacing[3]};
  }
`;

const FormSection = styled.div`
  margin-bottom: ${spacing[8]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${typography.fonts.display};
  font-size: ${typography.sizes.lg};
  font-weight: ${typography.weights.semibold};
  color: ${colors.sacred.shadow};
  margin-bottom: ${spacing[4]};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const FormField = styled.div`
  margin-bottom: ${spacing[4]};
`;

const Label = styled.label`
  display: block;
  font-size: ${typography.sizes.sm};
  font-weight: ${typography.weights.medium};
  color: ${colors.sacred.shadow};
  margin-bottom: ${spacing[2]};
`;

const Input = styled.input`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.sacred.mist};
  border-radius: ${spacing[2]};
  font-size: ${typography.sizes.base};
  transition: ${transitions.gentle};
  
  &:focus {
    outline: none;
    border-color: ${colors.sacred.gold};
    box-shadow: 0 0 0 3px ${colors.sacred.gold}20;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.sacred.mist};
  border-radius: ${spacing[2]};
  font-size: ${typography.sizes.base};
  background: white;
  cursor: pointer;
  transition: ${transitions.gentle};
  
  &:focus {
    outline: none;
    border-color: ${colors.sacred.gold};
    box-shadow: 0 0 0 3px ${colors.sacred.gold}20;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.sacred.mist};
  border-radius: ${spacing[2]};
  font-size: ${typography.sizes.base};
  min-height: 80px;
  resize: vertical;
  transition: ${transitions.gentle};
  
  &:focus {
    outline: none;
    border-color: ${colors.sacred.gold};
    box-shadow: 0 0 0 3px ${colors.sacred.gold}20;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${colors.sacred.gold};
`;

const CheckboxLabel = styled.label`
  font-size: ${typography.sizes.sm};
  color: ${colors.sacred.shadow};
  margin: 0;
`;

const TreatmentCard = styled.div`
  border: 1px solid ${colors.sacred.mist};
  border-radius: ${spacing[3]};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[3]};
  background: ${colors.sacred.whisper};
`;

const TreatmentName = styled.h4`
  font-weight: ${typography.weights.semibold};
  color: ${colors.sacred.shadow};
  margin: 0 0 ${spacing[1]} 0;
`;

const TreatmentDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${typography.sizes.sm};
  color: ${colors.sacred.shadow};
  opacity: 0.8;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[4]};
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: ${spacing[4]} ${spacing[6]};
  background: linear-gradient(135deg, ${colors.tiffany[500]} 0%, ${colors.tiffany[600]} 100%);
  color: ${colors.sacred.white};
  border: none;
  border-radius: ${spacing[3]};
  font-size: ${typography.sizes.base};
  font-weight: ${typography.weights.semibold};
  cursor: pointer;
  transition: ${transitions.gentle};
  margin-top: ${spacing[6]};
  box-shadow: ${shadows.soft};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.flowing};
    background: linear-gradient(135deg, ${colors.tiffany[600]} 0%, ${colors.tiffany[700]} 100%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    background: ${colors.sacred.stone};
  }
`;

const BookingModal = () => {
  const { t } = useTranslation();
  const { 
    isModalOpen, 
    closeBookingModal, 
    bookingData, 
    updateBookingData, 
    updateCustomerInfo,
    selectedTreatment 
  } = useBooking();

  const [selectedTreatmentId, setSelectedTreatmentId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use the treatment translations hook
  const { 
    getTranslatedTreatmentName, 
    getTranslatedTreatmentDescription, 
    getTranslatedCategory,
    loading: translationsLoading 
  } = useTreatmentTranslations();

  useEffect(() => {
    if (selectedTreatment) {
      setSelectedTreatmentId(selectedTreatment.id);
    }
  }, [selectedTreatment]);

  const handleTreatmentChange = (e) => {
    const treatmentId = e.target.value;
    setSelectedTreatmentId(treatmentId);
    
    if (treatmentId) {
      const treatment = treatments.find(t => t.id === treatmentId);
      updateBookingData({
        treatments: [treatment],
        duration: treatment.duration
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!bookingData.customerInfo.name || !bookingData.customerInfo.email || !bookingData.date || !bookingData.time) {
      alert(t('bookingModal.validation.requiredFields') || '🌙 Please fill in all required fields for your sacred journey! 🌙');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(bookingData.customerInfo.email)) {
      alert(t('bookingModal.validation.invalidEmail') || '🌙 Please enter a valid email address for your sacred wellness journey! 🌙');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Map booking data to API format
      const apiPayload = {
        // Personal Information
        name: bookingData.customerInfo.name,
        email: bookingData.customerInfo.email,
        phone: bookingData.customerInfo.phone || '',
        
        // Booking Details
        treatment: bookingData.treatments.length > 0 ? bookingData.treatments[0].name : 'General Consultation',
        preferredDate: bookingData.date,
        preferredTime: bookingData.time,
        
        // Additional Information
        specialRequests: bookingData.customerInfo.specialRequests || '',
        previousClient: !bookingData.customerInfo.isFirstTime,
        
        // Source tracking
        source: 'website_booking_modal'
      };

      console.log('🧘‍♀️ Submitting booking to API:', apiPayload);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload),
      });

      if (!response.ok) {
        throw new Error('Booking submission failed');
      }

      const result = await response.json();
      console.log('✅ Booking submission successful:', result);
      
      setIsSubmitting(false);
      closeBookingModal();
      
      // Show success message
      alert(t('bookingModal.messages.success') || '🌙✨ Your sacred booking request has been submitted! You will receive a confirmation email shortly and we will contact you within 24 hours to confirm your appointment. ✨🌙');
      
    } catch (error) {
      console.error('❌ Booking submission error:', error);
      setIsSubmitting(false);
      alert(t('bookingModal.messages.error') || '💫 Sacred energies are temporarily disrupted. Please try again or call us directly at +34 610 76 02 78! 💫');
    }
  };

  const selectedTreatmentData = treatments.find(t => t.id === selectedTreatmentId);

  return (
    <AnimatePresence>
      {isModalOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeBookingModal}
        >
          <ModalContainer
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ duration: 0.3, ease: easings.gentle }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>{t('bookingModal.title') || 'Book Your Spa Experience'}</ModalTitle>
              <CloseButton onClick={closeBookingModal}>
                <X size={24} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <form onSubmit={handleSubmit}>
                {/* Treatment Selection */}
                <FormSection>
                  <SectionTitle>
                    <Check size={20} />
                    {t('bookingModal.sections.treatment.title') || 'Select Treatment'}
                  </SectionTitle>
                  
                  <FormField>
                    <Label htmlFor="treatment">{t('bookingModal.sections.treatment.label') || 'Choose your treatment'}</Label>
                    <Select 
                      id="treatment"
                      value={selectedTreatmentId}
                      onChange={handleTreatmentChange}
                      required
                    >
                      <option value="">{t('bookingModal.sections.treatment.placeholder') || 'Select a treatment...'}</option>
                      {Object.entries(treatmentCategories).map(([key, englishCategory]) => {
                        const categoryLabel = getTranslatedCategory(key, englishCategory);
                        return (
                          <optgroup key={key} label={categoryLabel}>
                            {treatments
                              .filter(t => t.category === englishCategory)
                              .map(treatment => {
                                const treatmentName = getTranslatedTreatmentName(treatment.id, treatment.name);
                                const minutesText = t('bookingModal.sections.treatment.minutes') || 'min';
                                return (
                                  <option key={treatment.id} value={treatment.id}>
                                    {treatmentName} - €{treatment.price} ({treatment.duration}{minutesText})
                                  </option>
                                );
                              })
                            }
                          </optgroup>
                        );
                      })}
                    </Select>
                  </FormField>

                  {selectedTreatmentData && (
                    <TreatmentCard>
                      <TreatmentName>
                        {getTranslatedTreatmentName(selectedTreatmentData.id, selectedTreatmentData.name)}
                      </TreatmentName>
                      <p style={{ fontSize: typography.sizes.sm, margin: `${spacing[2]} 0`, color: colors.sacred.shadow, opacity: 0.8 }}>
                        {getTranslatedTreatmentDescription(selectedTreatmentData.id, selectedTreatmentData.description)}
                      </p>
                      <TreatmentDetails>
                        <span>{selectedTreatmentData.duration} {t('bookingModal.sections.treatment.minutes') || 'minutes'}</span>
                        <span>€{selectedTreatmentData.price}</span>
                      </TreatmentDetails>
                    </TreatmentCard>
                  )}
                </FormSection>

                {/* Date & Time */}
                <FormSection>
                  <SectionTitle>
                    <Calendar size={20} />
                    {t('bookingModal.sections.datetime.title') || 'Preferred Date & Time'}
                  </SectionTitle>
                  
                  <FormRow>
                    <FormField>
                      <Label htmlFor="date">{t('bookingModal.sections.datetime.dateLabel') || 'Preferred Date'}</Label>
                      <Input 
                        type="date"
                        id="date"
                        value={bookingData.date}
                        onChange={(e) => updateBookingData({ date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </FormField>
                    
                    <FormField>
                      <Label htmlFor="time">{t('bookingModal.sections.datetime.timeLabel') || 'Preferred Time'}</Label>
                      <Select 
                        id="time"
                        value={bookingData.time}
                        onChange={(e) => updateBookingData({ time: e.target.value })}
                        required
                      >
                        <option value="">{t('bookingModal.sections.datetime.timePlaceholder') || 'Select time...'}</option>
                        <option value="09:00">{t('bookingModal.sections.datetime.timeOptions.09:00') || '9:00 AM'}</option>
                        <option value="10:00">{t('bookingModal.sections.datetime.timeOptions.10:00') || '10:00 AM'}</option>
                        <option value="11:00">{t('bookingModal.sections.datetime.timeOptions.11:00') || '11:00 AM'}</option>
                        <option value="12:00">{t('bookingModal.sections.datetime.timeOptions.12:00') || '12:00 PM'}</option>
                        <option value="13:00">{t('bookingModal.sections.datetime.timeOptions.13:00') || '1:00 PM'}</option>
                        <option value="14:00">{t('bookingModal.sections.datetime.timeOptions.14:00') || '2:00 PM'}</option>
                        <option value="15:00">{t('bookingModal.sections.datetime.timeOptions.15:00') || '3:00 PM'}</option>
                        <option value="16:00">{t('bookingModal.sections.datetime.timeOptions.16:00') || '4:00 PM'}</option>
                        <option value="17:00">{t('bookingModal.sections.datetime.timeOptions.17:00') || '5:00 PM'}</option>
                        <option value="18:00">{t('bookingModal.sections.datetime.timeOptions.18:00') || '6:00 PM'}</option>
                      </Select>
                    </FormField>
                  </FormRow>
                </FormSection>

                {/* Contact Information */}
                <FormSection>
                  <SectionTitle>
                    <User size={20} />
                    {t('bookingModal.sections.contact.title') || 'Contact Information'}
                  </SectionTitle>
                  
                  <FormField>
                    <Label htmlFor="name">{t('bookingModal.sections.contact.nameLabel') || 'Full Name'}</Label>
                    <Input 
                      type="text"
                      id="name"
                      value={bookingData.customerInfo.name}
                      onChange={(e) => updateCustomerInfo({ name: e.target.value })}
                      required
                    />
                  </FormField>
                  
                  <FormRow>
                    <FormField>
                      <Label htmlFor="email">{t('bookingModal.sections.contact.emailLabel') || 'Email Address'}</Label>
                      <Input 
                        type="email"
                        id="email"
                        value={bookingData.customerInfo.email}
                        onChange={(e) => updateCustomerInfo({ email: e.target.value })}
                        required
                      />
                    </FormField>
                    
                    <FormField>
                      <Label htmlFor="phone">{t('bookingModal.sections.contact.phoneLabel') || 'Phone Number'}</Label>
                      <Input 
                        type="tel"
                        id="phone"
                        value={bookingData.customerInfo.phone}
                        onChange={(e) => updateCustomerInfo({ phone: e.target.value })}
                        required
                      />
                    </FormField>
                  </FormRow>

                  <FormField>
                    <Label htmlFor="requests">{t('bookingModal.sections.contact.requestsLabel') || 'Special Requests or Notes'}</Label>
                    <TextArea 
                      id="requests"
                      value={bookingData.customerInfo.specialRequests}
                      onChange={(e) => updateCustomerInfo({ specialRequests: e.target.value })}
                      placeholder={t('bookingModal.sections.contact.requestsPlaceholder') || 'Any allergies, preferences, or special requirements...'}
                    />
                  </FormField>

                  <CheckboxContainer>
                    <Checkbox 
                      type="checkbox"
                      id="firstTime"
                      checked={bookingData.customerInfo.isFirstTime}
                      onChange={(e) => updateCustomerInfo({ isFirstTime: e.target.checked })}
                    />
                    <CheckboxLabel htmlFor="firstTime">
                      {t('bookingModal.sections.contact.firstTimeLabel') || 'This is my first visit to Ama Wellness & Spa'}
                    </CheckboxLabel>
                  </CheckboxContainer>
                </FormSection>

                <SubmitButton 
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (t('bookingModal.buttons.submitting') || 'Submitting...') : (t('bookingModal.buttons.submit') || 'Request Booking')}
                </SubmitButton>
              </form>
            </ModalBody>
          </ModalContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default BookingModal; 
